import React, { useMemo, useState } from "react";
import { Button, Dialog, Grow, IconButton, TextField, Tooltip } from "@mui/material";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useToast } from "../Toast";
import { roleErrorMessage } from "../../utilities/projectRoles";
import "../Modal/modal.scss";
import "./index.scss";

const SmoothGrow = React.forwardRef(function SmoothGrow(props, ref) {
  return <Grow ref={ref} timeout={{ enter: 320, exit: 200 }} {...props} />;
});

const sameName = (a, b) =>
  String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

/**
 * Manage the roles one project uses.
 *
 * Roles are per project, so this only ever edits the project it was opened
 * from. A role somebody holds cannot be deleted — the server refuses and names
 * the members in the way, which is surfaced here as an error toast.
 */
const ManageRolesModal = ({
  open,
  handleClose = () => {},
  projectName = "this project",
  roles = [],
  loading = false,
  loadError = null,
  refreshRoles = async () => {},
  contributors = [],
  createRole = async () => {},
  renameRole = async () => {},
  deleteRole = async () => {},
}) => {
  const toast = useToast();
  const [newRole, setNewRole] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [busy, setBusy] = useState(false);

  /* How many people hold each role, so a role that cannot be deleted says so
     before anyone clicks the bin. */
  const memberCounts = useMemo(() => {
    const counts = {};
    (contributors || []).forEach((member) => {
      const role = String(member?.role || "").trim().toLowerCase();
      if (role) counts[role] = (counts[role] || 0) + 1;
    });
    return counts;
  }, [contributors]);

  const countFor = (role) => memberCounts[String(role?.name || "").trim().toLowerCase()] || 0;

  const resetRowState = () => {
    setEditingId(null);
    setEditingName("");
    setPendingDeleteId(null);
  };

  const closeDialog = () => {
    resetRowState();
    setNewRole("");
    handleClose();
  };

  const handleAdd = async () => {
    const name = newRole.trim();
    if (!name) return;

    if (roles.some((role) => sameName(role?.name, name))) {
      toast.error(`"${name}" is already a role on this project`);
      return;
    }

    setBusy(true);
    try {
      await createRole(name);
      setNewRole("");
      toast.success(`"${name}" added`);
    } catch (error) {
      toast.error(roleErrorMessage(error, "Could not add that role. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (role) => {
    setPendingDeleteId(null);
    setEditingId(role?._id);
    setEditingName(role?.name || "");
  };

  const handleSaveEdit = async (role) => {
    const name = editingName.trim();
    if (!name || sameName(name, role?.name)) {
      resetRowState();
      return;
    }

    setBusy(true);
    try {
      const updated = await renameRole(role._id, name);
      resetRowState();
      /* A rename moves everyone holding the old name across, so say how many
         people it touched rather than leaving that a surprise. */
      const moved = updated?.membersUpdated || 0;
      toast.success(
        moved > 0
          ? `Renamed to "${name}" · ${moved} ${moved === 1 ? "member" : "members"} updated`
          : `Renamed to "${name}"`
      );
    } catch (error) {
      toast.error(roleErrorMessage(error, "Could not rename that role. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (role) => {
    setBusy(true);
    try {
      await deleteRole(role._id);
      setPendingDeleteId(null);
      toast.success(`"${role.name}" removed`);
    } catch (error) {
      setPendingDeleteId(null);
      toast.error(roleErrorMessage(error, "Could not remove that role. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      aria-labelledby="manage-roles-dialog-title"
      maxWidth="sm"
      fullWidth
      TransitionComponent={SmoothGrow}
      PaperProps={{ className: "pj-modal pj-modal--form pj-modal--roles" }}
      slotProps={{ backdrop: { className: "pj-modal-backdrop" } }}
    >
      <div className="pj-modal-body">
        <IconButton className="pj-modal-close" size="small" onClick={closeDialog} aria-label="Close">
          <CloseRoundedIcon fontSize="small" />
        </IconButton>

        <div className="pj-modal-head">
          <span className="pj-modal-badge">
            <AdminPanelSettingsRoundedIcon />
          </span>
          <div>
            <h3 className="pj-modal-title" id="manage-roles-dialog-title">
              Manage roles
            </h3>
            <p className="pj-modal-sub">
              Roles people can hold on <strong>{projectName}</strong>
            </p>
          </div>
        </div>

        <div className="pj-field-group">
          <label className="pj-field-label" htmlFor="manage-roles-new">
            Add a role
          </label>
          <div className="pj-role-add">
            <TextField
              id="manage-roles-new"
              size="small"
              fullWidth
              placeholder="e.g. Release Manager"
              value={newRole}
              onChange={(event) => setNewRole(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleAdd();
                }
              }}
            />
            <Button
              className="pj-btn pj-btn--primary pj-role-add-btn"
              startIcon={<AddRoundedIcon />}
              onClick={handleAdd}
              disabled={busy || !newRole.trim() || Boolean(loadError)}
            >
              Add
            </Button>
          </div>
        </div>

        <div className="pj-field-group">
          <label className="pj-field-label">
            Project roles <span className="pj-role-count">{roles.length}</span>
          </label>

          {loadError ? (
            /* A failed request must not masquerade as "this project has no
               roles" — adding one would only fail again for the same reason. */
            <div className="pj-role-error" role="alert">
              <p>{loadError}</p>
              <Button className="pj-role-retry" size="small" onClick={() => refreshRoles()}>
                Try again
              </Button>
            </div>
          ) : loading && roles.length === 0 ? (
            <p className="pj-role-empty">Loading roles…</p>
          ) : roles.length === 0 ? (
            <p className="pj-role-empty">
              No roles yet. Add the first one above.
            </p>
          ) : (
            <ul className="pj-role-list" aria-label="Project roles">
              {roles.map((role) => {
                const inUse = countFor(role);
                const isEditing = editingId === role._id;
                const isConfirming = pendingDeleteId === role._id;

                return (
                  <li className="pj-role-row" key={role._id}>
                    {isEditing ? (
                      <>
                        <TextField
                          size="small"
                          fullWidth
                          autoFocus
                          value={editingName}
                          inputProps={{ "aria-label": `Rename ${role.name}` }}
                          onChange={(event) => setEditingName(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              handleSaveEdit(role);
                            }
                            if (event.key === "Escape") resetRowState();
                          }}
                        />
                        <div className="pj-role-actions">
                          <IconButton
                            className="pj-role-icon is-save"
                            size="small"
                            aria-label={`Save ${role.name}`}
                            disabled={busy}
                            onClick={() => handleSaveEdit(role)}
                          >
                            <CheckRoundedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            className="pj-role-icon"
                            size="small"
                            aria-label="Cancel rename"
                            onClick={resetRowState}
                          >
                            <CloseRoundedIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="pj-role-name">
                          <span>{role.name}</span>
                          {inUse > 0 ? (
                            <span className="pj-role-chip">
                              {inUse} {inUse === 1 ? "member" : "members"}
                            </span>
                          ) : null}
                        </div>

                        {isConfirming ? (
                          /* An inline confirm rather than a second dialog — one
                             backdrop, and the row stays in view while deciding */
                          <div className="pj-role-confirm">
                            <span>Remove?</span>
                            <Button
                              className="pj-role-confirm-btn is-yes"
                              size="small"
                              disabled={busy}
                              onClick={() => handleDelete(role)}
                            >
                              Yes
                            </Button>
                            <Button
                              className="pj-role-confirm-btn"
                              size="small"
                              onClick={() => setPendingDeleteId(null)}
                            >
                              No
                            </Button>
                          </div>
                        ) : (
                          <div className="pj-role-actions">
                            <IconButton
                              className="pj-role-icon"
                              size="small"
                              aria-label={`Rename ${role.name}`}
                              onClick={() => startEdit(role)}
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                            <Tooltip
                              title={inUse > 0 ? `${inUse} ${inUse === 1 ? "member holds" : "members hold"} this role` : ""}
                              disableHoverListener={inUse === 0}
                            >
                              <span>
                                <IconButton
                                  className="pj-role-icon is-delete"
                                  size="small"
                                  aria-label={`Remove ${role.name}`}
                                  disabled={inUse > 0}
                                  onClick={() => setPendingDeleteId(role._id)}
                                >
                                  <DeleteOutlineRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </div>
                        )}
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <div className="pj-modal-actions">
        <Button className="pj-btn pj-btn--primary" onClick={closeDialog}>
          Done
        </Button>
      </div>
    </Dialog>
  );
};

export default ManageRolesModal;
